// src/components/email/EmailTemplateManager.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  FiMail, FiEdit, FiTrash2, FiPlus, FiImage, FiUploadCloud,
  FiSave, FiX, FiEye, FiZap, FiPlusCircle, FiDownload, FiSend,
  FiRefreshCw, FiSettings, FiMonitor, FiSmartphone, FiInfo
} from 'react-icons/fi';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { emailService } from '../../utils/emailUtils';
import { getFunctions, httpsCallable } from 'firebase/functions';

const EmailTemplateManager = ({ onUseTemplate }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const organizationId = userProfile?.organizationId;
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [error, setError] = useState(null);
  const [headerImage, setHeaderImage] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const fileInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const quillRef = useRef(null);

  // Available modules - Generic business categories
  const moduleTypes = [
    { value: 'general', label: 'General Communication', icon: '💬' },
    { value: 'customer', label: 'Customer Communications', icon: '👤' },
    { value: 'order', label: 'Order & Service Updates', icon: '📦' },
    { value: 'billing', label: 'Billing & Invoices', icon: '💰' },
    { value: 'newsletter', label: 'Newsletter & Updates', icon: '📰' },
    { value: 'support', label: 'Support & Help', icon: '🆘' },
    { value: 'marketing', label: 'Marketing & Promotions', icon: '📢' },
    { value: 'notification', label: 'Notifications & Alerts', icon: '🔔' }
  ];

  // Email-safe Quill modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    }
  };

  const quillFormats = [
    'header', 'font',
    'bold', 'italic', 'underline', 'blockquote',
    'list', 'bullet',
    'link', 'image',
    'color', 'background', 'align'
  ];

  // Enhanced HTML sanitization for email templates
  const sanitizeHtmlForEmail = (html) => {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Convert to email-safe structure
    const emailSafeHtml = processHtmlForEmail(tempDiv);

    return emailSafeHtml.innerHTML;
  };

  const processHtmlForEmail = (element) => {
    // Convert divs to paragraphs or table cells for better email compatibility
    const divs = element.querySelectorAll('div');
    divs.forEach(div => {
      if (!div.querySelector('div, p, table')) {
        // Simple div, convert to paragraph
        const p = document.createElement('p');
        p.innerHTML = div.innerHTML;
        p.style.margin = '0 0 16px 0';
        div.parentNode.replaceChild(p, div);
      }
    });

    // Ensure all images are email-safe
    const images = element.querySelectorAll('img');
    images.forEach(img => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.setAttribute('border', '0');

      if (!img.alt) {
        img.alt = '';
      }
    });

    // Make links email-safe
    const links = element.querySelectorAll('a');
    links.forEach(link => {
      link.style.color = '#2563eb';
      link.style.textDecoration = 'underline';
    });

    // Convert lists to be more email-compatible
    const lists = element.querySelectorAll('ul, ol');
    lists.forEach(list => {
      list.style.paddingLeft = '30px';
      list.style.margin = '16px 0';
    });

    // Ensure paragraphs have proper spacing
    const paragraphs = element.querySelectorAll('p');
    paragraphs.forEach(p => {
      p.style.margin = '0 0 16px 0';
      p.style.lineHeight = '1.6';
    });

    return element;
  };

  // Generate email-safe template structure
  const generateEmailSafeTemplate = (content) => {
    const emailSafeStyles = `
      /* Email-safe CSS */
      body { 
        font-family: Arial, sans-serif; 
        line-height: 1.6; 
        color: #333333; 
        margin: 0; 
        padding: 0; 
        -webkit-text-size-adjust: 100%; 
        -ms-text-size-adjust: 100%; 
      }
      table { 
        border-collapse: collapse; 
        mso-table-lspace: 0pt; 
        mso-table-rspace: 0pt; 
      }
      img { 
        border: 0; 
        height: auto; 
        line-height: 100%; 
        outline: none; 
        text-decoration: none; 
        -ms-interpolation-mode: bicubic; 
        max-width: 100%;
      }
      .email-container { 
        max-width: 600px; 
        margin: 0 auto; 
        background-color: #ffffff; 
      }
      .email-content { 
        padding: 20px; 
      }
      .email-header {
        text-align: center;
        padding: 20px;
        background-color: ${userProfile?.primaryColor || '#2563eb'};
      }
      .email-footer {
        padding: 20px;
        background-color: #f8f9fa;
        border-top: 1px solid #dee2e6;
        text-align: center;
        font-size: 12px;
        color: #6c757d;
      }
      @media only screen and (max-width: 620px) {
        .email-container { 
          width: 95% !important; 
          max-width: 95% !important; 
        }
        .email-content, .email-header, .email-footer { 
          padding: 15px !important; 
        }
      }
    `;

    return `
      <!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
      <html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
      <head>
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="x-apple-disable-message-reformatting" />
        <!--[if !mso]><!-->
        <meta http-equiv="X-UA-Compatible" content="IE=edge" />
        <!--<![endif]-->
        <title>${editingTemplate?.subject || 'Email Template'}</title>
        <style type="text/css">
          ${emailSafeStyles}
        </style>
        <!--[if gte mso 9]>
        <xml>
          <o:OfficeDocumentSettings>
            <o:AllowPNG/>
            <o:PixelsPerInch>96</o:PixelsPerInch>
          </o:OfficeDocumentSettings>
        </xml>
        <![endif]-->
      </head>
      <body style="margin: 0; padding: 0; background-color: #f4f4f4;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f4f4f4;">
          <tr>
            <td align="center" style="padding: 20px 0;">
              <table role="presentation" class="email-container" cellspacing="0" cellpadding="0" border="0" style="max-width: 600px; background-color: #ffffff; border-radius: 8px;">
                ${headerImage ? `
                <tr>
                  <td class="email-header" style="text-align: center; padding: 0;">
                    <img src="${headerImage}" alt="${userProfile?.organizationName || 'Header'}" style="width: 100%; max-width: 600px; height: auto; display: block;" />
                  </td>
                </tr>
                ` : ''}
                <tr>
                  <td class="email-content" style="padding: 20px;">
                    ${sanitizeHtmlForEmail(content)}
                  </td>
                </tr>
                <tr>
                  <td class="email-footer" style="padding: 20px; background-color: #f8f9fa; border-top: 1px solid #dee2e6; text-align: center; font-size: 12px; color: #6c757d;">
                    <p style="margin: 0;">
                      This email was sent from {{organization.name}}
                      {{#if organization.email}} | <a href="mailto:{{organization.email}}" style="color: #6c757d;">{{organization.email}}</a>{{/if}}
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;
  };

  // Load templates on component mount
  useEffect(() => {
    if (organizationId) {
      fetchTemplates();
    }
  }, [organizationId]);

  // Fetch templates from the server
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const results = [];

      for (const module of moduleTypes) {
        try {
          const templateData = await emailService.getEmailTemplate(organizationId, module.value);

          if (templateData.template) {
            results.push({
              id: module.value,
              ...templateData.template,
              source: templateData.source
            });
          }
        } catch (err) {
          console.error(`Error fetching ${module.value} template:`, err);
        }
      }

      setTemplates(results);
    } catch (err) {
      setError("Failed to load templates");
      console.error("Error loading templates:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (templateId) => {
    if (onUseTemplate) {
      onUseTemplate(templateId);
    } else {
      navigate('/email/compose', { state: { templateId } });
    }
  };

  // Start editing a template
  const handleEditTemplate = (template) => {
    setEditingTemplate({
      ...template,
      module: template.id || template.type
    });
    setHeaderImage(template.headerImageUrl);
    setAttachments(template.attachments || []);
    setPreviewMode(false);
    setShowAiPanel(false);
  };

  // Create a new template
  const handleCreateTemplate = (moduleType) => {
    setEditingTemplate({
      id: null,
      name: `${moduleType.label} Template`,
      module: moduleType.value,
      type: moduleType.value,
      subject: '',
      htmlContent: getDefaultTemplateContent(moduleType.value),
      headerImageUrl: null,
      attachments: []
    });
    setHeaderImage(null);
    setAttachments([]);
    setPreviewMode(false);
    setShowAiPanel(false);
  };

  // Get default template content with email-safe structure
  const getDefaultTemplateContent = (moduleType) => {
    return `
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          <td style="padding: 20px 0; text-align: center;">
            <h2 style="color: #333333; margin: 0 0 20px 0; font-family: Arial, sans-serif;">
              Welcome to {{organization.name}}
            </h2>
          </td>
        </tr>
        <tr>
          <td style="padding: 0 0 20px 0;">
            <p style="color: #333333; font-family: Arial, sans-serif; line-height: 1.6; margin: 0 0 16px 0;">
              Dear {{customer.name}},
            </p>
            <div style="color: #333333; font-family: Arial, sans-serif; line-height: 1.6;">
              {{content}}
            </div>
            <p style="color: #333333; font-family: Arial, sans-serif; line-height: 1.6; margin: 16px 0 0 0;">
              Best regards,<br/>
              The {{organization.name}} Team
            </p>
          </td>
        </tr>
      </table>
    `;
  };

  // AI Template Generation
  const handleAIGeneration = async () => {
    if (!aiPrompt.trim()) {
      setError('Please enter a prompt for AI generation');
      return;
    }

    try {
      setAiLoading(true);
      setError(null);

      const functions = getFunctions();
      const generateTemplate = httpsCallable(functions, 'generateEmailTemplate');

      const result = await generateTemplate({
        prompt: aiPrompt,
        templateType: editingTemplate.module,
        organizationInfo: {
          name: userProfile?.organizationName || 'Your Organization',
          primaryColor: userProfile?.primaryColor || '#2563eb',
          email: userProfile?.email
        },
        includeHeader: true,
        includeFooter: true,
        colorScheme: 'professional',
        tone: 'professional'
      });

      if (result.data.success) {
        setEditingTemplate(prev => ({
          ...prev,
          htmlContent: result.data.htmlContent
        }));
        setShowAiPanel(false);
        setAiPrompt('');
      } else {
        throw new Error('AI generation failed');
      }
    } catch (err) {
      setError(`AI Generation Error: ${err.message}`);
      console.error('AI generation error:', err);
    } finally {
      setAiLoading(false);
    }
  };

  // Get preview HTML with proper email structure and variable replacement
  const getPreviewHtml = () => {
    if (!editingTemplate || !editingTemplate.htmlContent) {
      return '<p style="color: #666; text-align: center; padding: 40px;">No template content to preview</p>';
    }

    let previewContent = editingTemplate.htmlContent;

    // Replace common variables with sample data
    const replacements = {
      '{{organization.name}}': userProfile?.organizationName || 'Your Organization',
      '{{organization.email}}': userProfile?.email || 'contact@yourorganization.com',
      '{{organization.phone}}': userProfile?.phone || '+1 (555) 123-4567',
      '{{organization.address}}': '123 Business Street, City, State 12345',
      '{{headerImageUrl}}': headerImage || 'https://via.placeholder.com/600x150/2563eb/ffffff?text=Email+Header',
      '{{content}}': '<p style="margin: 0 0 16px 0; line-height: 1.6; color: #333333;">This is sample content that would be inserted into the email template. It demonstrates how the template will look when real content is added.</p>',
      '{{customer.name}}': 'John Smith',
      '{{customer.email}}': 'john.smith@example.com',
      '{{order.number}}': 'ORD-2025-1234',
      '{{account.manager}}': 'Sarah Johnson'
    };

    // Module-specific replacements
    if (editingTemplate.module === 'billing') {
      replacements['{{invoice.number}}'] = 'INV-2025-5678';
      replacements['{{invoice.amount}}'] = '$150.00';
      replacements['{{invoice.dueDate}}'] = 'May 25, 2025';
      replacements['{{invoice.paymentLink}}'] = '#payment-link';
    } else if (editingTemplate.module === 'order') {
      replacements['{{order.number}}'] = 'ORD-2025-9876';
      replacements['{{order.total}}'] = '$299.99';
      replacements['{{order.status}}'] = 'Processing';
      replacements['{{tracking.number}}'] = 'TRK123456789';
    }

    Object.entries(replacements).forEach(([placeholder, value]) => {
      previewContent = previewContent.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
    });

    // If the content doesn't have a complete HTML structure, wrap it in a basic container
    if (!previewContent.includes('<!DOCTYPE') && !previewContent.includes('<html')) {
      previewContent = `
        <div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; line-height: 1.6; color: #333333; background-color: #ffffff;">
          ${previewContent}
        </div>
      `;
    }

    // Remove any DOCTYPE and HTML tags for inline rendering
    previewContent = previewContent.replace(/<!DOCTYPE[^>]*>/gi, '');
    previewContent = previewContent.replace(/<\/?html[^>]*>/gi, '');
    previewContent = previewContent.replace(/<head>[\s\S]*?<\/head>/gi, '');
    previewContent = previewContent.replace(/<\/?body[^>]*>/gi, '');

    return previewContent;
  };
  // Enhanced preview rendering with iframe for better email simulation
  const renderPreview = () => {
    const previewHtml = getPreviewHtml();

    return (
      <div className="h-full p-6">
        <div className="flex justify-center mb-4">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setPreviewDevice('desktop')}
              className={`px-3 py-1 rounded text-sm flex items-center ${previewDevice === 'desktop' ? 'bg-white shadow' : ''
                }`}
            >
              <FiMonitor className="mr-1" size={14} />
              Desktop
            </button>
            <button
              onClick={() => setPreviewDevice('mobile')}
              className={`px-3 py-1 rounded text-sm flex items-center ${previewDevice === 'mobile' ? 'bg-white shadow' : ''
                }`}
            >
              <FiSmartphone className="mr-1" size={14} />
              Mobile
            </button>
          </div>
        </div>

        <div className="flex justify-center">
          <div
            className={`bg-white border border-gray-200 rounded-lg overflow-hidden shadow-lg ${previewDevice === 'mobile' ? 'max-w-sm' : 'max-w-4xl w-full'
              }`}
            style={{ minHeight: '500px' }}
          >
            <div className="p-4 bg-gray-50 border-b text-sm text-gray-600">
              <div><strong>Template:</strong> {editingTemplate.name}</div>
              <div><strong>Subject:</strong> {editingTemplate.subject || 'Email Subject'}</div>
              <div><strong>Module:</strong> {editingTemplate.module}</div>
            </div>
            <div className="overflow-auto" style={{ maxHeight: '600px' }}>
              {/* Use dangerouslySetInnerHTML for better rendering */}
              <div
                className="template-preview-content p-4"
                style={{
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: '1.6',
                  color: '#333333',
                  backgroundColor: '#ffffff'
                }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Save the template with enhanced processing
  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;

    try {
      setLoading(true);

      // Process the HTML content for email safety before saving
      const processedHtmlContent = sanitizeHtmlForEmail(editingTemplate.htmlContent);

      const updatedTemplate = await emailService.saveEmailTemplate(
        organizationId,
        editingTemplate.module,
        {
          ...editingTemplate,
          htmlContent: processedHtmlContent,
          headerImageUrl: headerImage,
          attachments: attachments
        }
      );

      await fetchTemplates();
      setEditingTemplate(null);
      setError(null);
    } catch (err) {
      setError("Failed to save template");
      console.error("Error saving template:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle template form field changes
  const handleTemplateChange = (e) => {
    const { name, value } = e.target;
    setEditingTemplate(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle rich text content change with email-safe processing
  const handleContentChange = (content) => {
    setEditingTemplate(prev => ({
      ...prev,
      htmlContent: content
    }));
  };

  // Handle file selection for header image
  const handleImageSelect = async (e) => {
    if (!e.target.files || !e.target.files.length) return;

    const file = e.target.files[0];

    try {
      const imageUrl = await emailService.uploadTemplateHeaderImage(
        organizationId,
        editingTemplate.module,
        file
      );

      setHeaderImage(imageUrl);
    } catch (err) {
      setError("Failed to upload image");
      console.error("Error uploading image:", err);
    }
  };

  // Handle attachment selection
  const handleAttachmentSelect = async (e) => {
    if (!e.target.files || !e.target.files.length) return;

    const files = Array.from(e.target.files);

    try {
      const newAttachments = [];

      for (const file of files) {
        const attachmentUrl = await emailService.uploadTemplateAttachment(
          organizationId,
          editingTemplate.module,
          file
        );

        newAttachments.push({
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          url: attachmentUrl
        });
      }

      setAttachments(prev => [...prev, ...newAttachments]);
    } catch (err) {
      setError("Failed to upload attachments");
      console.error("Error uploading attachments:", err);
    }
  };

  // Remove attachment
  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  // Export template
  const handleExportTemplate = () => {
    if (!editingTemplate) return;

    const templateData = {
      ...editingTemplate,
      headerImageUrl: headerImage,
      attachments: attachments,
      htmlContent: sanitizeHtmlForEmail(editingTemplate.htmlContent)
    };

    const dataStr = JSON.stringify(templateData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `${editingTemplate.name.replace(/\s+/g, '-').toLowerCase()}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Render AI panel
  const renderAIPanel = () => {
    if (!showAiPanel) return null;

    return (
      <div className="border-t border-gray-200 p-4 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center mb-3">
          <Sparkles className="text-purple-600 mr-2" size={18} />
          <h3 className="font-medium text-purple-800">AI Template Assistant</h3>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Describe the email template you want to create:
            </label>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              rows={3}
              placeholder="E.g., Create a professional invoice email with payment button and company branding..."
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-gray-500">Quick suggestions:</span>
            {getAIPromptSuggestions().map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleAIPromptSuggestion(suggestion)}
                className="text-xs px-2 py-1 bg-white border border-gray-200 rounded-full hover:bg-purple-50 hover:border-purple-200 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>

          <div className="flex justify-between items-center">
            <button
              onClick={() => setShowAiPanel(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleAIGeneration}
              disabled={aiLoading || !aiPrompt.trim()}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {aiLoading ? (
                <>
                  <FiRefreshCw className="animate-spin mr-2" size={14} />
                  Generating...
                </>
              ) : (
                <>
                  <FiZap className="mr-2" size={14} />
                  Generate Template
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Handle AI prompt suggestions
  const handleAIPromptSuggestion = (suggestion) => {
    setAiPrompt(suggestion);
  };

  // Get AI prompt suggestions based on template type
  const getAIPromptSuggestions = () => {
    const suggestions = {
      customer: [
        'Create a warm welcome email for new customers',
        'Draft a customer onboarding template',
        'Generate a customer support follow-up email'
      ],
      billing: [
        'Create a professional invoice email template',
        'Draft a payment reminder with payment options',
        'Generate a receipt confirmation email'
      ],
      order: [
        'Create an order confirmation template',
        'Draft a shipping notification email',
        'Generate an order delivery update'
      ],
      newsletter: [
        'Create a monthly newsletter template',
        'Draft a product update announcement',
        'Generate an educational content template'
      ],
      marketing: [
        'Create a promotional email template',
        'Draft a special offer announcement',
        'Generate a product launch email'
      ]
    };

    return suggestions[editingTemplate?.module] || [
      'Create a professional email template',
      'Draft a formal communication template',
      'Generate a branded email layout'
    ];
  };

  // Render the editor view - keeping the structure but with enhanced HTML processing
  const renderTemplateEditor = () => {
    if (!editingTemplate) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[95vh] flex flex-col overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">
              {editingTemplate.id ? 'Edit Template' : 'Create Template'}
            </h2>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAiPanel(!showAiPanel)}
                className={`p-2 rounded-lg ${showAiPanel ? 'bg-purple-100 text-purple-600' : 'hover:bg-gray-100'}`}
                title="AI Assistant"
              >
                <Sparkles size={18} />
              </button>
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className={`p-2 rounded-lg ${previewMode ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
                title="Preview"
              >
                <FiEye size={18} />
              </button>
              <button
                onClick={handleExportTemplate}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Export Template"
              >
                <FiDownload size={18} />
              </button>
              <button
                onClick={() => setEditingTemplate(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <FiX size={18} />
              </button>
            </div>
          </div>

          {/* AI Panel */}
          {renderAIPanel()}

          <div className="flex-1 overflow-auto">
            {previewMode ? (
              renderPreview()
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                {/* Left Column - Template Settings */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editingTemplate.name || ''}
                      onChange={handleTemplateChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={editingTemplate.subject || ''}
                      onChange={handleTemplateChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Email subject line..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Module Type
                    </label>
                    <select
                      name="module"
                      value={editingTemplate.module}
                      onChange={handleTemplateChange}
                      disabled={!!editingTemplate.id}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {moduleTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center justify-between text-sm font-medium text-gray-700 mb-1">
                      <span>Header Image</span>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-sm text-blue-600 flex items-center"
                      >
                        <FiUploadCloud className="mr-1" size={14} />
                        Upload Image
                      </button>
                    </label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageSelect}
                      accept="image/*"
                      className="hidden"
                    />
                    {headerImage ? (
                      <div className="border border-gray-300 rounded-md overflow-hidden">
                        <img
                          src={headerImage}
                          alt="Email header"
                          className="w-full h-32 object-cover"
                        />
                      </div>
                    ) : (
                      <div className="border border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center justify-center text-gray-500">
                        <FiImage size={24} className="mb-2" />
                        <p className="text-sm">No header image selected</p>
                        <p className="text-xs">Recommended size: 600px × 150px</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="flex items-center justify-between text-sm font-medium text-gray-700 mb-1">
                      <span>Attachments</span>
                      <button
                        onClick={() => attachmentInputRef.current?.click()}
                        className="text-sm text-blue-600 flex items-center"
                      >
                        <FiPlusCircle className="mr-1" size={14} />
                        Add Files
                      </button>
                    </label>
                    <input
                      type="file"
                      ref={attachmentInputRef}
                      onChange={handleAttachmentSelect}
                      multiple
                      className="hidden"
                    />

                    {attachments.length > 0 ? (
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {attachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                            <div className="flex items-center">
                              <FiImage size={16} className="text-gray-400 mr-2" />
                              <div>
                                <div className="text-sm font-medium">{attachment.name}</div>
                                <div className="text-xs text-gray-500">
                                  {(attachment.size / 1024).toFixed(1)} KB
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveAttachment(attachment.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FiX size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="border border-dashed border-gray-300 rounded-md p-4 text-center text-gray-500">
                        <p className="text-sm">No attachments added</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Rich Text Editor */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Content
                    </label>
                    <div className="border border-gray-300 rounded-md">
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={editingTemplate.htmlContent || ''}
                        onChange={handleContentChange}
                        modules={quillModules}
                        formats={quillFormats}
                        style={{ height: '400px' }}
                      />
                    </div>
                  </div>

                  <div className="mt-16 p-4 bg-gray-50 rounded-md">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Available Variables</h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="space-y-1">
                        <div><code>{'{{organization.name}}'}</code> - Organization name</div>
                        <div><code>{'{{organization.email}}'}</code> - Organization email</div>
                        <div><code>{'{{organization.phone}}'}</code> - Organization phone</div>
                        <div><code>{'{{headerImageUrl}}'}</code> - Header image</div>
                        <div><code>{'{{content}}'}</code> - Main content</div>
                      </div>
                      <div className="space-y-1">
                        {editingTemplate.module === 'customer' && (
                          <>
                            <div><code>{'{{customer.name}}'}</code> - Customer name</div>
                            <div><code>{'{{customer.email}}'}</code> - Customer email</div>
                            <div><code>{'{{order.number}}'}</code> - Order number</div>
                            <div><code>{'{{account.manager}}'}</code> - Account manager</div>
                          </>
                        )}
                        {editingTemplate.module === 'billing' && (
                          <>
                            <div><code>{'{{invoice.number}}'}</code> - Invoice #</div>
                            <div><code>{'{{invoice.amount}}'}</code> - Amount</div>
                            <div><code>{'{{invoice.dueDate}}'}</code> - Due date</div>
                            <div><code>{'{{invoice.paymentLink}}'}</code> - Payment link</div>
                          </>
                        )}
                        {editingTemplate.module === 'order' && (
                          <>
                            <div><code>{'{{order.number}}'}</code> - Order number</div>
                            <div><code>{'{{order.total}}'}</code> - Order total</div>
                            <div><code>{'{{order.status}}'}</code> - Order status</div>
                            <div><code>{'{{tracking.number}}'}</code> - Tracking #</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Email Client Compatibility Info */}
                  <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                      <FiInfo className="mr-2" size={14} />
                      Email Client Compatibility
                    </h4>
                    <div className="text-xs text-blue-700 space-y-1">
                      <div>✅ Tables for layout ensure compatibility across all email clients</div>
                      <div>✅ Inline styles work consistently in Gmail, Outlook, Apple Mail</div>
                      <div>✅ Web-safe fonts (Arial, sans-serif) display reliably</div>
                      <div>✅ 600px max width fits most email preview panes</div>
                      <div>⚠️ Background images may not display in some clients</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowAiPanel(!showAiPanel)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md shadow-sm hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center"
              >
                <Sparkles className="mr-2" size={16} />
                AI Assistant
              </button>

              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md shadow-sm hover:bg-gray-50 transition-colors flex items-center"
              >
                <FiEye className="mr-2" size={16} />
                {previewMode ? 'Edit' : 'Preview'}
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setEditingTemplate(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md shadow-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <FiRefreshCw className="animate-spin mr-2" size={16} />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave className="mr-2" size={16} />
                    Save Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render template list
  const renderTemplateList = () => {
    if (loading) {
      return (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      );
    }

    if (templates.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center">
            <FiMail className="text-purple-600" size={32} />
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">No email templates found</h3>
          <p className="text-gray-500 mb-6">Create your first AI-powered email template</p>
          <button
            onClick={() => handleCreateTemplate(moduleTypes[0])}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md shadow-sm hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center mx-auto"
          >
            <FiZap className="mr-2" size={16} />
            Create with AI
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {moduleTypes.map(moduleType => {
          const moduleTemplate = templates.find(t =>
            t.id === moduleType.value ||
            t.module === moduleType.value ||
            t.type === moduleType.value
          );

          return (
            <div
              key={moduleType.value}
              className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="px-6 py-4 flex justify-between items-center bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{moduleType.icon}</span>
                  <h3 className="font-medium">{moduleType.label}</h3>
                </div>
                <div className="flex items-center space-x-2">

                  {moduleTemplate ? (
                    <>
                      <button
                        onClick={() => handleUseTemplate(moduleTemplate.id)}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <FiSend className="mr-1" size={14} />
                        Use Template
                      </button>

                      <button
                        onClick={() => handleEditTemplate(moduleTemplate)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <FiEdit size={16} />
                      </button>
                      <button
                        onClick={() => {/* Handle duplicate */ }}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      >
                        <FiPlus size={16} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleCreateTemplate(moduleType)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <FiZap className="mr-1" size={14} />
                      Create with AI
                    </button>
                  )}
                </div>
              </div>

              {moduleTemplate ? (
                <div className="p-6">
                  <div className="flex items-start">
                    {moduleTemplate.headerImageUrl ? (
                      <div className="w-20 h-16 rounded overflow-hidden mr-4 border border-gray-200 flex-shrink-0">
                        <img
                          src={moduleTemplate.headerImageUrl}
                          alt="Template header"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-16 rounded mr-4 bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
                        <FiImage size={20} className="text-gray-400" />
                      </div>
                    )}

                    <div className="flex-1">
                      <h4 className="font-medium mb-1">{moduleTemplate.name}</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Subject: {moduleTemplate.subject || 'No subject set'}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          {moduleTemplate.source === 'system' ? (
                            <span className="italic">System default</span>
                          ) : moduleTemplate.source === 'default' ? (
                            <span className="italic">Auto-generated</span>
                          ) : (
                            <span>Custom template</span>
                          )}
                        </span>
                        {moduleTemplate.attachments && moduleTemplate.attachments.length > 0 && (
                          <span className="flex items-center">
                            <FiImage className="mr-1" size={12} />
                            {moduleTemplate.attachments.length} attachments
                          </span>
                        )}
                      </div>

                      {/* Email client compatibility indicator */}
                      <div className="mt-2 flex items-center text-xs">
                        <div className="flex items-center text-green-600">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                          Email-safe format
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                    <FiZap size={20} className="text-gray-400" />
                  </div>
                  <p className="text-sm">No template created yet</p>
                  <p className="text-xs text-gray-400">Use AI to generate a professional template</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            AI Email Templates
          </h1>
          <p className="text-gray-600 mt-2">Create professional, email-client compatible templates with AI assistance</p>
          <p className="text-sm text-gray-500 mt-1">✅ Optimized for Gmail, Outlook, Apple Mail, and other major email clients</p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => {/* Handle import */ }}
            className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
          >
            <FiUploadCloud className="mr-2" size={16} />
            Import Template
          </button>

          <button
            onClick={() => handleCreateTemplate(moduleTypes[0])}
            className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center"
          >
            <FiZap className="mr-2" size={16} />
            Create with AI
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center">
          <FiX className="mr-2" size={16} />
          {error}
        </div>
      )}

      {renderTemplateList()}
      {editingTemplate && renderTemplateEditor()}
    </div>
  );
};

export default EmailTemplateManager;